import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { exchangeBalance } from '@app/api/balance/useExchange';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';
import { MASTER_BANK_ID, REFERRAL_BANK_ID } from '@utils/static';

const EXCLUDE_BANK_IDS = [MASTER_BANK_ID, REFERRAL_BANK_ID];

const aggregateBalancesByCurrency = (wallets) =>
  wallets.reduce((acc, wallet) => {
    const { currency, balance } = wallet;
    if (!currency) {
      return acc;
    }

    acc[currency] = (acc[currency] || 0) + (Number(balance) || 0);
    return acc;
  }, {});

async function calculateBinBalanceInUsd(balancesByCurrency) {
  const toConvert = Object.entries(balancesByCurrency).filter(
    ([currency, balance]) => currency !== 'USD' && Number(balance) > 0,
  );

  const results = await Promise.allSettled(
    toConvert.map(([currency, balance]) =>
      exchangeBalance({
        type: 'transfer',
        from: currency,
        to: 'USD',
        amount: Number(balance),
      }),
    ),
  );

  if (results.some((result) => result.status === 'rejected')) {
    showToaster('error', i18n.t('request_error'));
  }

  return results.reduce(
    (total, result) =>
      total + (result.status === 'fulfilled' ? Number(result.value) || 0 : 0),
    Number(balancesByCurrency.USD) || 0,
  );
}

export const fetchBankWallets = createAsyncThunk(
  'bankWallets/fetchBankWallets',
  async (_, { rejectWithValue }) => {
    try {
      const res = await instance.get('/bank/wallets?with_cards=true');
      const wallets = res?.data ?? [];

      const binWallets = wallets.filter(
        (wallet) => !EXCLUDE_BANK_IDS.includes(wallet.bank_id),
      );
      const binBalance = await calculateBinBalanceInUsd(
        aggregateBalancesByCurrency(binWallets),
      );

      const master = wallets.find((item) => item.bank_id === MASTER_BANK_ID);
      const masterBalance = Number(master?.balance) || 0;
      const totalBalance = masterBalance + binBalance;

      return { wallets, binWallets, masterBalance, binBalance, totalBalance };
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.message);
    }
  },
);

const bankWalletsSlice = createSlice({
  name: 'bankWallets',
  initialState: {
    bankWallets: [],
    transferBankWallets: [],
    referralWithdrawWallets: [],
    totalBalance: 0,
    masterBalance: 0,
    binBalance: 0,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBankWallets.fulfilled, (state, action) => {
      const { wallets, binWallets, masterBalance, binBalance, totalBalance } =
        action.payload;

      state.bankWallets = binWallets;
      state.transferBankWallets = wallets.filter(
        (wallet) => wallet.bank_id !== REFERRAL_BANK_ID,
      );
      state.referralWithdrawWallets = wallets.filter((wallet) =>
        EXCLUDE_BANK_IDS.includes(wallet.bank_id),
      );
      state.masterBalance = masterBalance;
      state.binBalance = binBalance;
      state.totalBalance = totalBalance;
    });
  },
});

export default bankWalletsSlice.reducer;
